import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { Modal } from '@/components/modal/modal';
import { Form } from '@/components/form';
import { RadioGroup } from '@/components/inputs/radio-group';
import { PhoneInput } from '@/components/inputs/phone-input';
import { Button } from '@/components/button';
import { Banner } from '@/components/banner';
import { useTranslation } from '@/contexts/i18n-context';
import { TranslationKey } from '@/contexts/translations';
import { optionalString, requiredPhoneNumber } from '@/util/validators';
import { formatPhoneNumber } from '@/util/helpers';
import { useTfaSms as useCreateTfaSms } from '@/api/create-sms-tfa-option';
import { useTfaSms as useUpdateTfaSms } from '@/api/update-sms-tfa-option';
import { CreateSmsTfaOptionErrorType, UpdateSmsTfaOptionErrorType } from '@/api/api.schemas';
import styles from './sms-tfa-phone-dialog.module.css';

const OTHER_PHONE_VALUE = 'other';
const translationBase = 'patientInformation.tfa';

export type SmsTfaPhoneDialogMode = 'add' | 'change';

function createSchema(hasProfilePhones: boolean) {
  return z
    .object({
      selectedPhone: optionalString().nullish(),
      phoneNumber: optionalString().nullish(),
    })
    .superRefine((data, ctx) => {
      if (hasProfilePhones && !data.selectedPhone) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'common.errors.notSelectedOption',
          path: ['selectedPhone'],
        });
      }

      const needsPhoneInput = !hasProfilePhones || data.selectedPhone === OTHER_PHONE_VALUE;
      if (!needsPhoneInput) {
        return;
      }

      const result = requiredPhoneNumber().safeParse(data.phoneNumber ?? '');
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: issue.message,
            path: ['phoneNumber'],
          });
        }
      }
    });
}

type FormValues = z.infer<ReturnType<typeof createSchema>>;

function getDefaultValues(profilePhones: string[], currentPhone?: string): FormValues | undefined {
  if (profilePhones.length === 0) {
    return {
      phoneNumber: currentPhone ? formatPhoneNumber(currentPhone) : '',
    };
  }

  if (currentPhone && profilePhones.includes(currentPhone)) {
    return { selectedPhone: currentPhone, phoneNumber: '' };
  }

  if (currentPhone) {
    return {
      selectedPhone: OTHER_PHONE_VALUE,
      phoneNumber: formatPhoneNumber(currentPhone),
    };
  }
}

interface SmsTfaPhoneDialogProps {
  isOpen: boolean;
  mode: SmsTfaPhoneDialogMode;
  currentPhone?: string;
  phoneNumbers: string[];
  onClose: () => void;
  onSuccess: () => void;
}

export function SmsTfaPhoneDialog({
  mode,
  currentPhone,
  phoneNumbers,
  onClose,
  onSuccess,
}: SmsTfaPhoneDialogProps) {
  const { t } = useTranslation();

  return (
    <Modal isOpen onClose={onClose} closeOnOutsideClick>
      <h2 className="h4">
        {t(`${translationBase}.dialog.${phoneNumbers.length > 0 ? 'title' : 'noPhoneTitle'}`)}
      </h2>
      <SmsTfaPhoneDialogForm
        mode={mode}
        currentPhone={currentPhone}
        phoneNumbers={phoneNumbers}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </Modal>
  );
}

function SmsTfaPhoneDialogForm({
  mode,
  currentPhone,
  phoneNumbers,
  onClose,
  onSuccess,
}: Omit<SmsTfaPhoneDialogProps, 'isOpen'>) {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<TranslationKey>();
  const { mutateAsync: createSmsTfa, isPending: isCreating } = useCreateTfaSms();
  const { mutateAsync: updateSmsTfa, isPending: isUpdating } = useUpdateTfaSms();

  const hasProfilePhones = phoneNumbers.length > 0;
  const schema = useMemo(() => createSchema(hasProfilePhones), [hasProfilePhones]);
  const defaultValues = useMemo(
    () => getDefaultValues(phoneNumbers, currentPhone),
    [phoneNumbers, currentPhone],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues,
  });

  const selectedPhone = form.watch('selectedPhone');
  const showPhoneInput = !hasProfilePhones || selectedPhone === OTHER_PHONE_VALUE;
  const isPending = isCreating || isUpdating;

  const onSubmit = async (data: FormValues) => {
    setSubmitError(undefined);

    const phoneNumber =
      hasProfilePhones && data.selectedPhone && data.selectedPhone !== OTHER_PHONE_VALUE
        ? data.selectedPhone
        : data.phoneNumber;

    if (!phoneNumber) {
      setSubmitError(`${translationBase}.errors.invalidPhone`);
      return;
    }

    try {
      if (mode === 'add') {
        await createSmsTfa({ data: { phoneNumber } });
      } else {
        await updateSmsTfa({ data: { phoneNumber } });
      }

      onSuccess();
      onClose();
    } catch (error) {
      setSubmitError(getSmsTfaErrorKey(error));
    }
  };

  const radioOptions = [
    ...phoneNumbers.map((phone) => ({
      value: phone,
      label: formatPhoneNumber(phone),
    })),
    { value: OTHER_PHONE_VALUE, label: t('common.other') },
  ];

  return (
    <Form form={form} onSubmit={onSubmit} className={styles.tfaDialogForm} autoComplete="off">
      {submitError && <Banner type="error">{t(submitError)}</Banner>}

      {hasProfilePhones && (
        <RadioGroup
          name="selectedPhone"
          label={t(`${translationBase}.dialog.optionsLabel`)}
          readersOnlyLabel
          className={styles.smsTfaPhoneRadioGroup}
          options={radioOptions}
          defaultCheckedValue={defaultValues?.selectedPhone ?? undefined}
        />
      )}

      {showPhoneInput && (
        <PhoneInput
          name="phoneNumber"
          readersOnlyLabel={phoneNumbers.length === 0}
          label={t(`${translationBase}.dialog.phoneLabel`)}
        />
      )}

      <Button type="submit" loading={isPending}>
        {t(`${translationBase}.dialog.confirm`)}
      </Button>
    </Form>
  );
}

function getSmsTfaErrorKey(error: unknown): TranslationKey {
  const type = (error as { type?: string } | undefined)?.type;

  if (
    type === CreateSmsTfaOptionErrorType.InvalidPhoneNumber ||
    type === UpdateSmsTfaOptionErrorType.InvalidPhoneNumber
  ) {
    return `${translationBase}.errors.invalidPhone`;
  }

  if (type === CreateSmsTfaOptionErrorType.AlreadyConfigured) {
    return `${translationBase}.errors.alreadyConfigured`;
  }

  if (type === UpdateSmsTfaOptionErrorType.NotConfigured) {
    return `${translationBase}.errors.notConfigured`;
  }

  return 'common.errors.unknown';
}
